"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useServiceRequestFlow = void 0;
const react_1 = require("react");
const useCustomers_1 = require("./useCustomers");
const useServiceCompanies_1 = require("./useServiceCompanies");
const useServiceRequests_1 = require("./useServiceRequests");
const queries_1 = require("../graphql/queries");
const types_1 = require("../types");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useServiceRequestFlow = (initialState) => {
    const [state, setState] = (0, react_1.useState)({
        step: initialState?.step ?? 'customer',
        customer: initialState?.customer ?? null,
        serviceCompany: initialState?.serviceCompany ?? null,
        serviceRequest: initialState?.serviceRequest ?? null,
    });
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [searchResults, setSearchResults] = (0, react_1.useState)([]);
    const [hasSearched, setHasSearched] = (0, react_1.useState)(false);
    const [lastSearchedEmail, setLastSearchedEmail] = (0, react_1.useState)('');
    const { customers } = (0, useCustomers_1.useCustomers)();
    const { create: createCustomer } = (0, useCustomers_1.useCreateCustomer)();
    const { serviceCompanies } = (0, useServiceCompanies_1.useServiceCompanies)();
    const { create: createServiceRequest } = (0, useServiceRequests_1.useCreateServiceRequest)();
    const { update: updateServiceRequest } = (0, useServiceRequests_1.useUpdateServiceRequest)();
    const { requestService: requestWaterLeakService } = (0, useServiceRequests_1.useWaterLeakService)();
    const processInitialState = () => {
        if (!initialState || !loading) {
            return;
        }
        const customer = customers.find((customer) => customer.id === initialState.customerId);
        const serviceCompany = serviceCompanies.find((company) => company.id === initialState.serviceCompanyId);
        if (!customer) {
            return;
        }
        if (!serviceCompany) {
            setState((prev) => ({ ...prev, customer, step: 'serviceCompany' }));
        }
        if (serviceCompany) {
            setState((prev) => ({
                ...prev,
                customer,
                serviceCompany,
                step: 'serviceRequest',
            }));
        }
        setLoading(false);
    };
    const findCustomerByEmail = async (email) => {
        try {
            setLoading(true);
            setError(null);
            setHasSearched(true);
            setLastSearchedEmail(email);
            if (!email || email.trim() === '') {
                setSearchResults(customers);
                return customers;
            }
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listCustomersOverview,
                variables: { text: email, limit: 100 },
                authMode: 'userPool',
            });
            let matchingCustomers = [];
            if (response && response.data && response.data.listCustomersOverview !== undefined) {
                let items = [];
                if (Array.isArray(response.data.listCustomersOverview)) {
                    items = response.data.listCustomersOverview;
                }
                else if (response.data.listCustomersOverview &&
                    Array.isArray(response.data.listCustomersOverview.items)) {
                    items = response.data.listCustomersOverview.items;
                }
                matchingCustomers = items.map((item) => item.customer || item);
            }
            setSearchResults(matchingCustomers);
            return matchingCustomers;
        }
        catch (err) {
            setError(err);
            console.error('Error finding customer by email:', err);
            setSearchResults([]);
            return [];
        }
        finally {
            setLoading(false);
        }
    };
    const addNewCustomer = async (customerData) => {
        try {
            setLoading(true);
            setError(null);
            const newCustomer = await createCustomer(customerData);
            if (newCustomer) {
                setState((prev) => ({
                    ...prev,
                    customer: newCustomer,
                    step: 'serviceCompany',
                }));
                return newCustomer;
            }
            return null;
        }
        catch (err) {
            setError(err);
            console.error('Error creating new customer:', err);
            return null;
        }
        finally {
            setLoading(false);
        }
    };
    const selectCustomer = (customer) => {
        setState((prev) => ({
            ...prev,
            customer,
            step: 'serviceCompany',
        }));
    };
    // Step 2: Select service company
    const selectServiceCompany = (serviceCompany) => {
        setState((prev) => ({ ...prev, serviceCompany, step: 'serviceRequest' }));
        return serviceCompany;
    };
    // Step 3: Create service request
    const createNewServiceRequest = async (formData) => {
        try {
            if (!state.customer || !state.serviceCompany) {
                setError(new Error('Customer and service company must be selected before creating a service request'));
                return null;
            }
            setLoading(true);
            setError(null);
            const input = {
                customerId: state.customer.id,
                serviceType: formData.serviceType || 'Other',
                reason: formData.description || 'Service requested',
                hubId: formData.hubId,
                sensorId: formData.sensorId,
                alertId: formData.alertId,
                status: formData.status || types_1.ServiceRequestStatus.PENDING,
                priority: formData.urgency,
                scheduledAt: formData.requestedDate
                    ? new Date(formData.requestedDate).getTime()
                    : undefined,
                notes: formData.notes,
                serviceCompanyId: state.serviceCompany.id,
                customerComplaint: formData.customerComplaint,
                customerNotes: formData.customerNotes,
                potentialCause: formData.potentialCause,
                accessInstructions: formData.accessInstructions,
                additionalNotes: formData.additionalNotes,
                technicianName: formData.technicianName,
                deviceLocation: formData.deviceLocation,
                roomType: formData.roomType,
            };
            Object.keys(input).forEach((key) => {
                if (input[key] === undefined) {
                    delete input[key];
                }
            });
            const isLeakService = formData.serviceType === types_1.ServiceType.LEAK ||
                formData.serviceType === types_1.ServiceType.LEAK_DETECTOR_SERVICE ||
                formData.serviceType?.toLowerCase().includes('leak');
            let newServiceRequest;
            if (isLeakService && formData.hubId && formData.sensorId) {
                const waterLeakInput = {
                    hubId: formData.hubId,
                    sensorId: formData.sensorId,
                    alertId: formData.alertId,
                    priority: formData.urgency || 'High',
                    notes: formData.notes,
                    accessInstructions: formData.accessInstructions,
                    serviceCompanyId: state.serviceCompany.id,
                };
                newServiceRequest = await requestWaterLeakService(waterLeakInput);
            }
            else {
                newServiceRequest = await createServiceRequest(input);
            }
            if (newServiceRequest) {
                const enrichedServiceRequest = {
                    ...newServiceRequest,
                    title: formData.title,
                    description: formData.description,
                    notes: formData.notes,
                    requestedTimeWindow: formData.requestedTimeWindow,
                    urgency: formData.urgency,
                    customer: state.customer,
                    serviceCompany: state.serviceCompany,
                };
                setState((prev) => ({
                    ...prev,
                    serviceRequest: enrichedServiceRequest,
                    step: 'confirmation',
                }));
                return enrichedServiceRequest;
            }
            return null;
        }
        catch (err) {
            setError(err);
            console.error('Error creating service request:', err);
            return null;
        }
        finally {
            setLoading(false);
        }
    };
    const updateExistingServiceRequest = async (input) => {
        try {
            setLoading(true);
            setError(null);
            const updatedServiceRequest = await updateServiceRequest(input);
            if (updatedServiceRequest) {
                if (state.serviceRequest && state.serviceRequest.id === updatedServiceRequest.id) {
                    setState((prev) => ({
                        ...prev,
                        serviceRequest: updatedServiceRequest,
                    }));
                }
                return updatedServiceRequest;
            }
            return null;
        }
        catch (err) {
            setError(err);
            console.error('Error updating service request:', err);
            return null;
        }
        finally {
            setLoading(false);
        }
    };
    const confirmServiceRequest = async () => {
        try {
            if (!state.serviceRequest) {
                setError(new Error('No service request to confirm'));
                return null;
            }
            setLoading(true);
            setError(null);
            const confirmedServiceRequest = { ...state.serviceRequest };
            setState((prev) => ({
                ...prev,
                serviceRequest: confirmedServiceRequest,
                step: 'complete',
            }));
            return confirmedServiceRequest;
        }
        catch (err) {
            setError(err);
            console.error('Error confirming service request:', err);
            return null;
        }
        finally {
            setLoading(false);
        }
    };
    const resetFlow = () => {
        setState({
            step: 'customer',
            customer: null,
            serviceCompany: null,
            serviceRequest: null,
        });
        setError(null);
    };
    const goBack = () => {
        switch (state.step) {
            case 'serviceCompany':
                setState((prev) => ({ ...prev, step: 'customer' }));
                break;
            case 'serviceRequest':
                setState((prev) => ({ ...prev, step: 'serviceCompany' }));
                break;
            case 'confirmation':
                setState((prev) => ({ ...prev, step: 'serviceRequest' }));
                break;
            default:
                break;
        }
    };
    (0, react_1.useEffect)(() => {
        if (!initialState?.customerId && !initialState?.serviceCompanyId) {
            return;
        }
        if (customers.length === 0 && serviceCompanies.length === 0) {
            setLoading(true);
            setError(null);
        }
        if (customers.length > 0 || serviceCompanies.length > 0) {
            processInitialState();
        }
    }, [initialState, customers, serviceCompanies]);
    return {
        state,
        loading,
        error,
        customers: hasSearched ? searchResults : customers,
        serviceCompanies,
        hasSearched,
        lastSearchedEmail,
        findCustomerByEmail,
        selectCustomer,
        addNewCustomer,
        selectServiceCompany,
        createNewServiceRequest,
        updateExistingServiceRequest,
        confirmServiceRequest,
        resetFlow,
        goBack,
    };
};
exports.useServiceRequestFlow = useServiceRequestFlow;
